<template>
  <div>
    <div v-if="loading.partner && !partner" class="h-100 d-flex justify-center align-center">
      <v-progress-circular :size="50" color="primary" indeterminate/>
    </div>

    <BackBtn />
    <BreadCrumbs :items="breadcrumbs" />

    <div v-if="partner && isSelf" class="mb-4">
      <h1 class="font-lg">Личный кабинет</h1>
      <div class="grey--text text--darken-1">{{ fullName }} · {{ partnerTypeLabel(partner.partner_type) }}</div>
    </div>

    <ProfileTopPanel v-if="partner && !isSelf"
      userTypeName="Партнер"
      :name="fullName"
      :city="partner.city"
      :phone="partner.phone"
      :email="partner.email"
      :company="partner.company"
      permssionEdit="edit_partner"
      permssionDelete="delete_partner"
      whoDelete="партнера"
      @delete="onDelete()" @edit="onEdit()"
    >
      <div class="profile-header-item" style="margin-top:0">
        <span class="mr-4">Тип: {{ partnerTypeLabel(partner.partner_type) }}</span>
        <v-switch v-if="$store.getters['auth/isAllowed']('edit_partner')"
          :input-value="partner.send_mail" @change="changeSendMail" :loading="loading.editMail"
          class="v-input--reverse profile-switch ml-4" hide-details :color="partner.send_mail ? 'success' : 'error'"
        >
          <div slot="label" :class="partner.send_mail ? 'success--text' : 'error--text'">Почта</div>
        </v-switch>
      </div>
    </ProfileTopPanel>

    <template v-if="partner">
      <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px">
        <v-tabs :value="currentTab" class="card-tabs" slider-size="3" optional>
          <v-tab v-for="tab in tabs" :key="tab.value" :tab-value="tab.value" @click="openTab(tab.value)">{{ tab.title }}</v-tab>
        </v-tabs>
        <div class="card-actions">
          <v-btn v-if="canDistribute && currentTab === 'overview'" color="primary" @click="dialogDistributeShow = true">
            Распределить из буфера
          </v-btn>
        </div>
      </div>

      <div v-if="currentTab === 'overview'" class="row">
        <div class="col-12 col-md-6">
          <ProfileCard title="Реквизиты" class="mb-5">
            <div v-for="row in requisites" :key="row.label" class="requisite-row">
              <div class="requisite-row__label">{{ row.label }}</div>
              <div class="requisite-row__value">{{ row.value || '—' }}</div>
            </div>
          </ProfileCard>
          <template v-if="PARTNER_FEATURES">
            <ClientProfileCardRef class="mb-5" />
            <ClientProfileCardCodesUsed class="mb-5" />
            <ProfileCardSearch class="mb-5" />
            <ProfileCardCalc />
          </template>
        </div>
        <div class="col-12 col-md-6">
          <ProfileCardExpansionCounter
            @add="dialogAddLicencesShow=true"
            :enableAdd="$store.getters['users/counters/getEndpoint']('partner') &&( $store.getters['auth/isAllowed']('add_stockamount_partner') || $store.getters['auth/isAllowed']('add_stockamount') )"
            :user="partner"
            :userType="'partner'"
          />
        </div>
      </div>

      <PartnerClients v-else-if="currentTab === 'clients'" :partnerId="userId" :self="isSelf" />
      <ManagersTable v-else-if="currentTab === 'managers'" />
      <RefLinks v-else-if="currentTab === 'links'" embedded :userId="userId" :userType="PARTNER" />
      <OrdersTable v-else-if="currentTab === 'orders'" mode="my" />
    </template>

    <PartnerEditDialog />
    <ManagerCreateModal v-if="isSelf" />
    <AddLicensesDialog v-model="dialogAddLicencesShow" :toId="+userId" toType='partner' @success="reload()" />
    <PartnerDistributeDialog v-if="canDistribute" v-model="dialogDistributeShow" :partnerId="userId" @success="reload()" />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import ProfileTopPanel from '@/components/common/ProfileTopPanel';
import PartnerEditDialog from '@/components/partners/PartnerEditDialog';
import PartnerDistributeDialog from '@/components/partners/profile/PartnerDistributeDialog.vue';
import PartnerClients from '@/components/partners/profile/PartnerClients.vue';
import ManagersTable from '@/components/managers/table/ManagersTable.vue';
import ManagerCreateModal from '@/components/managers/ManagerCreateModal';
import RefLinks from '@/views/refprogram/RefLinks.vue';
import OrdersTable from '@/components/orders/OrdersTable.vue';
import AddLicensesDialog from '@/components/admins/AddLicensesDialog.vue';
import ClientProfileCardRef from '@/components/clients/profile/ProfileCardRef';
import ClientProfileCardCodesUsed from '@/components/clients/profile/ProfileCardCodesUsed';
import ProfileCardSearch from '@/components/partners/profile/ProfileCardSearch';
import ProfileCardCalc from '@/components/admins/profile/ProfileCardCalc';
import ProfileCardExpansionCounter from '@/components/clients/profile/ProfileCardExpansionCounter.vue';
import { PARTNER_FEATURES } from '@/common/constants/features';
import { PARTNER } from '@/common/constants/userTypes';
import { PARTNER_TYPES, partnerTypeLabel } from '@/common/models/partnerTypes';

const TABS = [
  { value: 'overview', title: 'Обзор' },
  {
    value: 'managers', title: 'Менеджеры', permission: 'bookmark_managers', selfOnly: true,
  },
  { value: 'clients', title: 'Корпоративные клиенты', permission: 'bookmark_corporate_clients' },
  { value: 'links', title: 'Ссылки', selfOnly: true },
  { value: 'orders', title: 'Заказы', selfOnly: true },
];

// Карточка партнёра — один компонент для двух зрителей: админ открывает её из списка (/partners/:id?tab=),
// партнёр видит её как свой «Личный кабинет» (/profile?tab=). Вкладки и действия в том же виде, что у корпа.
export default {
  name: 'ProfilePartner',
  components: {
    ProfileCardExpansionCounter,
    PartnerEditDialog,
    PartnerDistributeDialog,
    PartnerClients,
    ManagersTable,
    ManagerCreateModal,
    RefLinks,
    OrdersTable,
    AddLicensesDialog,
    ClientProfileCardRef,
    ClientProfileCardCodesUsed,
    ProfileTopPanel,
    ProfileCardSearch,
    ProfileCardCalc,
  },
  props: ['userId'],
  provide() {
    return {
      userType: 'Partner',
      userId: this.userId,
    };
  },
  inject: {
    isProfile: {
      default: false,
    },
  },
  data() {
    return {
      PARTNER_FEATURES,
      PARTNER,
      dialogAddLicencesShow: false,
      dialogDistributeShow: false,
    };
  },
  computed: {
    ...mapGetters({
      loadedPartner: 'users/partners/partner',
      loading: 'users/partners/loading',
      user: 'auth/user',
    }),
    // В store может лежать ранее открытый партнёр: показываем только того, чей кабинет открыт
    partner() {
      const item = this.loadedPartner;
      return item && String(item.id) === String(this.userId) ? item : null;
    },
    // Свой кабинет партнёра (в отличие от карточки партнёра у админа)
    isSelf() {
      return this.isProfile && !!this.user && this.user.user_type === PARTNER;
    },
    // Распределяет только сам партнёр в своём кабинете
    // Реферал только привлекает клиентов: распределять из буфера ему не предлагаем
    canDistribute() {
      return this.isSelf && String(this.user.id) === String(this.userId)
        && !!this.partner && this.partner.partner_type !== PARTNER_TYPES.REFERRER;
    },
    tabs() {
      // Ссылки — список «своих» ссылок, а не ссылок партнёра из карточки: показываем только в личном кабинете
      return TABS
        .filter((t) => !t.selfOnly || this.isSelf)
        .filter((t) => !this.isSelf || !t.permission || this.$store.getters['auth/isAllowed'](t.permission));
    },
    currentTab() {
      const { tab } = this.$route.query;
      return this.tabs.some((t) => t.value === tab) ? tab : 'overview';
    },
    fullName() {
      return this.partner ? [this.partner.last_name, this.partner.first_name].filter(Boolean).join(' ') : '';
    },
    requisites() {
      if (!this.partner) return [];
      return [
        { label: 'Партнёр', value: this.fullName },
        { label: 'Тип', value: partnerTypeLabel(this.partner.partner_type) },
        { label: 'Email', value: this.partner.email },
        { label: 'Телефон', value: this.partner.phone },
      ];
    },
    breadcrumbs() {
      if (!this.partner) return [];
      return this.isProfile ? [
        { title: 'Личный кабинет', url: '/' },
      ] : [
        { title: 'Партнеры', url: '/partners/' },
        { title: this.fullName, url: `/partners/${this.partner.id}` },
      ];
    },
  },
  methods: {
    partnerTypeLabel,
    ...mapActions({
      loadPartner: 'users/partners/loadPartner',
      setDialogEditShow: 'users/partners/setDialogEditShow',
      setPartnerSendMail: 'users/partners/setPartnerSendMail',
      refreshMe: 'auth/refreshMe',
    }),
    ...mapMutations({
      setEditedPartner: 'users/partners/setEditedPartner',
    }),
    openTab(tab) {
      if (tab === this.currentTab) return;
      this.$router.push({ query: tab === 'overview' ? {} : { tab } });
    },
    onEdit() {
      this.setEditedPartner({ ...this.partner });
      this.setDialogEditShow(true);
    },
    onDelete() {
    },
    reload() {
      this.loadPartner(this.userId);
      // Шапка берёт остаток из профиля пользователя: обновляем его вместе с кабинетом
      if (this.isSelf) this.refreshMe();
    },
    changeSendMail(value) {
      this.setPartnerSendMail({ send_mail: !!value, id: this.partner.id, email: this.partner.email });
    },
  },
  created() {
    this.reload();
  },
};
</script>

<style scoped lang="scss">
@import "src/assets/styles/variables";

.card-tabs {
  flex: 1 1 auto;
  width: auto;
}

.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

// Вкладки как в карточке корпа: обычный регистр, полужирные
::v-deep .v-tab {
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600;
  font-size: 15px;
}

.requisite-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 0;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }

  &__label {
    color: #757575;
  }

  &__value {
    text-align: right;
    font-weight: 500;
  }
}
</style>
